import { useRequest } from 'features/request/useRequest';
import { ProjectMethodologyEnum } from 'gql/graphql';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { bimDeleteContractOtherCostGql, uerDeleteContractOtherCostGql } from '../contractOthersQueries';

interface IUseDeleteContractOtherCost {
    contractOtherCostId: number;
    methodology: ProjectMethodologyEnum;
}

export const useDeleteContractOtherCost = (props: IUseDeleteContractOtherCost) => {
    const { contractOtherCostId, methodology } = props;
    const dispatch = useDispatch();

    const variables = useMemo(() => ({ contractOtherCostId }), [contractOtherCostId]);

    const isUer = useMemo(() => methodology === ProjectMethodologyEnum.Uer, [methodology]);

    const [
        uerDeleteContractOtherCostData,
        uerDeleteContractOtherCostStatus,
        uerDeleteContractOtherCostError,
        uerDeleteContractOtherCostMakeRequest,
        uerDeleteContractOtherCostSetData,
    ] = useRequest({
        requestId: `deleteContractOtherCost${contractOtherCostId}`,
        query: uerDeleteContractOtherCostGql,
        withEffect: false,
    });

    const [
        bimDeleteContractOtherCostData,
        bimDeleteContractOtherCostStatus,
        bimDeleteContractOtherCostError,
        bimDeleteContractOtherCostMakeRequest,
        bimDeleteContractOtherCostSetData,
    ] = useRequest({
        requestId: `deleteContractOtherCost${contractOtherCostId}`,
        query: bimDeleteContractOtherCostGql,
        withEffect: false,
    });

    const makeRequest = useCallback(() => {
        if (isUer) {
            dispatch(uerDeleteContractOtherCostMakeRequest(variables));
        } else {
            dispatch(bimDeleteContractOtherCostMakeRequest(variables));
        }
    }, [isUer, dispatch, uerDeleteContractOtherCostMakeRequest, variables, bimDeleteContractOtherCostMakeRequest]);

    const reset = useCallback(() => {
        if (isUer) {
            dispatch(uerDeleteContractOtherCostSetData(undefined));
        } else {
            dispatch(bimDeleteContractOtherCostSetData(undefined));
        }
    }, [bimDeleteContractOtherCostSetData, uerDeleteContractOtherCostSetData, dispatch, isUer]);

    const data = useMemo(
        () =>
            isUer
                ? uerDeleteContractOtherCostData?.uerDeleteContractOtherCost
                : bimDeleteContractOtherCostData?.bimDeleteContractOtherCost,
        [
            bimDeleteContractOtherCostData?.bimDeleteContractOtherCost,
            isUer,
            uerDeleteContractOtherCostData?.uerDeleteContractOtherCost,
        ],
    );

    const status = useMemo(
        () => (isUer ? uerDeleteContractOtherCostStatus : bimDeleteContractOtherCostStatus),
        [isUer, uerDeleteContractOtherCostStatus, bimDeleteContractOtherCostStatus],
    );

    const error = useMemo(
        () => (isUer ? uerDeleteContractOtherCostError : bimDeleteContractOtherCostError),
        [isUer, uerDeleteContractOtherCostError, bimDeleteContractOtherCostError],
    );

    return { makeRequest, reset, data, status, error };
};
